export interface SearchFields {
  orderId: number | undefined;
  orderNumber: string;
  policyNumber: string;
  fileNumber: string;
  invoiceNumber: string;
  batchReferenceNumber: string;
  accountNumber: number | undefined;
  lockboxId: number | null;
  orderStatus: number | undefined;
  fromOrderDate: string;
  toOrderDate: string;
  stateCode: string;
}

export interface ValidationResult {
  valid: boolean;
  message?: string;
}

export const UNRESTRICTED_STATUS_IDS = [2954, 2955];

export const orderStatusOptions = [
  { name: 'Open', value: 2950 },
  { name: 'Submitted', value: 2951 },
  { name: 'Closed', value: 2952 },
  { name: 'Book Error', value: 2953 },
  { name: 'Upload In Process', value: 2954 },
  { name: 'Upload Complete', value: 2955 },
  { name: 'Cancelled', value: 2956 },
];

export function validateSearchCombination(fields: SearchFields): ValidationResult {
  // Strip non-digits from invoiceNumber so "INV-12345" → "12345" before any validation logic.
  // If the user entered letters only (e.g. "ABC"), the stripped value is empty — reject with a message.
  const strippedInvoice = fields.invoiceNumber?.replace(/\D/g, '') ?? '';
  if (fields.invoiceNumber && !strippedInvoice) {
    return { valid: false, message: 'Invoice Number must contain at least one digit.' };
  }

  // orderId, policyNumber, fileNumber, invoiceNumber are unique identifiers — self-sufficient search keys
  // orderNumber and batchReferenceNumber are filters (equality match against non-unique columns)
  const hasUniqueId = !!(fields.orderId || fields.policyNumber || fields.fileNumber || strippedInvoice);
  const hasOrderNum = !!fields.orderNumber;
  const hasBatchRef = !!fields.batchReferenceNumber;
  const hasAccount = !!fields.accountNumber;
  const hasStatus = fields.orderStatus !== undefined && fields.orderStatus !== null;
  const hasLockbox = !!fields.lockboxId;
  const hasDate = !!(fields.fromOrderDate && fields.toOrderDate);
  const hasAnyDate = !!(fields.fromOrderDate || fields.toOrderDate);
  const hasStateCode = !!fields.stateCode;
  const hasFileNumber = !!fields.fileNumber;
  const hasAny =
    hasUniqueId || hasOrderNum || hasBatchRef || hasAccount || hasStatus || hasLockbox || hasDate || hasStateCode;

  if (!hasAny) return { valid: false };

  // State Code check MUST come before the hasUniqueId early-exit:
  // stateCode + orderId (without fileNumber) must be invalid.
  if (hasStateCode && !hasFileNumber) {
    return { valid: false, message: 'State Code requires File Number.' };
  }

  if (hasUniqueId || hasOrderNum || hasBatchRef) return { valid: true };

  if (hasAccount && !hasStatus && !hasLockbox) {
    return {
      valid: false,
      message: 'Account Number requires Order Status, Lockbox, or Policy Number.',
    };
  }

  if (hasLockbox && !hasAccount && !hasAnyDate) {
    return { valid: false, message: 'Lockbox requires an Account Number or a date range.' };
  }

  if (hasStatus && !hasAccount) {
    if (!UNRESTRICTED_STATUS_IDS.includes(fields.orderStatus!)) {
      const statusName = orderStatusOptions.find((o) => o.value === fields.orderStatus)?.name ?? 'Selected status';
      return { valid: false, message: `${statusName} requires Account Number.` };
    }
  }

  return { valid: true };
}
